
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateResponse} from './ConfigUpdateResponse';
import {FieldModelConfigUpdateResponse} from './ConfigUpdateResponse';
import {SecuritySettings} from '../core/SecuritySettings';
import {FieldModelSecuritySettings} from '../core/SecuritySettings';

/**
 * SecuritySettingsDeleteResponse struct
 */
class SecuritySettingsDeleteResponse extends ConfigUpdateResponse {
  /**
   * Initialize struct
   * @param {!ConfigUpdateResponse=} parent
   * @param {!SecuritySettings=} securitySettings
   * @constructor
   */
  constructor (parent = new ConfigUpdateResponse(), securitySettings = new SecuritySettings()) {
    super()
    super.copy(parent)
    this.SecuritySettings = securitySettings
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SecuritySettingsDeleteResponse}
   * @param {!SecuritySettingsDeleteResponse} other Other struct
   * @returns {!SecuritySettingsDeleteResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.SecuritySettings != null) {
      this.SecuritySettings = SecuritySettings.fromObject(other.SecuritySettings)
    } else {
      this.SecuritySettings = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SecuritySettingsDeleteResponse}
   * @returns {!SecuritySettingsDeleteResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SecuritySettingsDeleteResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SecuritySettingsDeleteResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SecuritySettingsDeleteResponse}
   * @param {!SecuritySettingsDeleteResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SecuritySettingsDeleteResponse)) {
      throw new TypeError('Instance of SecuritySettingsDeleteResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SecuritySettingsDeleteResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      SecuritySettings: ((this.SecuritySettings != null) ? this.SecuritySettings : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SecuritySettingsDeleteResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SecuritySettingsDeleteResponse} other Object value
   * @returns {!SecuritySettingsDeleteResponse} Created struct
   */
  static fromObject (other) {
    return new SecuritySettingsDeleteResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SecuritySettingsDeleteResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SecuritySettingsDeleteResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SecuritySettingsDeleteResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6541
  }
}

export { SecuritySettingsDeleteResponse };

/**
 * Fast Binary Encoding SecuritySettingsDeleteResponse field model
 */
class FieldModelSecuritySettingsDeleteResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateResponse(buffer, 4 + 4)
    this.SecuritySettings = new FieldModelSecuritySettings(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
  }

  /**
   * Get the ConfigUpdateResponse field model
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @returns {!FieldModelConfigUpdateResponse} ConfigUpdateResponse field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.SecuritySettings.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.SecuritySettings.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSecuritySettingsDeleteResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6541
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SecuritySettings.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SecuritySettings.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SecuritySettings.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @param {!SecuritySettingsDeleteResponse} fbeValue Default value, defaults is new SecuritySettingsDeleteResponse()
   * @returns {!SecuritySettingsDeleteResponse} SecuritySettingsDeleteResponse value
   */
  get (fbeValue = new SecuritySettingsDeleteResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @param {!SecuritySettingsDeleteResponse} fbeValue SecuritySettingsDeleteResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SecuritySettings.fbeSize) <= fbeStructSize) {
      fbeValue.SecuritySettings = this.SecuritySettings.get()
    } else {
      fbeValue.SecuritySettings = new SecuritySettings()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SecuritySettings.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @param {!SecuritySettingsDeleteResponse} fbeValue SecuritySettingsDeleteResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSecuritySettingsDeleteResponse}
   * @param {SecuritySettingsDeleteResponse} fbeValue SecuritySettingsDeleteResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.SecuritySettings.set(fbeValue.SecuritySettings)
  }
}

export { FieldModelSecuritySettingsDeleteResponse };

/**
 * Fast Binary Encoding SecuritySettingsDeleteResponse model
 */
class SecuritySettingsDeleteResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSecuritySettingsDeleteResponse(this.buffer, 4)
  }

  /**
   * Get the SecuritySettingsDeleteResponse model
   * @this {!SecuritySettingsDeleteResponseModel}
   * @returns {!FieldModelSecuritySettingsDeleteResponse} model SecuritySettingsDeleteResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SecuritySettingsDeleteResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SecuritySettingsDeleteResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SecuritySettingsDeleteResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SecuritySettingsDeleteResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSecuritySettingsDeleteResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SecuritySettingsDeleteResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SecuritySettingsDeleteResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SecuritySettingsDeleteResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SecuritySettingsDeleteResponseModel}
   * @param {!SecuritySettingsDeleteResponse} value SecuritySettingsDeleteResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SecuritySettingsDeleteResponseModel}
   * @param {!SecuritySettingsDeleteResponse} value SecuritySettingsDeleteResponse value, defaults is new SecuritySettingsDeleteResponse()
   * @return {!object} Deserialized SecuritySettingsDeleteResponse value and its size
   */
  deserialize (value = new SecuritySettingsDeleteResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SecuritySettingsDeleteResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SecuritySettingsDeleteResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SecuritySettingsDeleteResponseModel}
   * @param {!number} prev Previous SecuritySettingsDeleteResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SecuritySettingsDeleteResponseModel };
